import React, { useEffect, useState } from "react";
import { Scale, X, Plus, Building2, Car, Eye, Clock as ClockIcon, EyeOff as EyeOffIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatTL, formatNum } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DateInput } from "@/components/DateInput";
import { PrimMonthPicker, primPeriod } from "./PrimMonth";

// (varlık, şehir_disi) → oran %
function autoRate(varlik, sehirDisi) {
  if (varlik && !sehirDisi) return 1.5;
  if (varlik && sehirDisi) return 2.5;
  if (!varlik && !sehirDisi) return 15;
  return 25;
}

export default function PrimModal({ editing, personnel, onClose, onSaved }) {
  const { canSeePrimRate, isPersonel, isAdmin, isMuhasebeci, user } = useAuth();
  const canAddCreditor = isAdmin || isMuhasebeci;
  const myPersonnelId = user?.personnel_id || "";
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState(editing ? {
    ...editing,
    giden_kisiler: [...(editing.giden_kisiler || []), "", "", "", ""].slice(0, 4),
    arastiran_kisi_id: editing.arastiran_kisi_id || "",
    icra_dairesi: editing.icra_dairesi || "",
    uyap_url: editing.uyap_url || "",
    sehir_disi: !!editing.sehir_disi,
    tags: Array.isArray(editing.tags) ? [...editing.tags] : [],
  } : {
    tarih: today,
    icra_foy: "",
    icra_esas: "",
    icra_dairesi: "",
    uyap_url: "",
    dosya_adi: "",
    alacakli_adi: "",
    varlik_sirketi: false,
    sehir_disi: false,
    oran: 15,
    vekalet_ucreti: "",
    tahsilat_miktari: "",
    // Personel: 1. giden otomatik kendisi, diğer 3 slot boş; admin/muhasebeci: 4 boş slot
    giden_kisiler: (isPersonel && myPersonnelId)
      ? [myPersonnelId, "", "", ""]
      : ["", "", "", ""],
    arastiran_kisi_id: "",
    aciklama: "",
    tags: [],
  });
  const [saving, setSaving] = useState(false);
  const [creditors, setCreditors] = useState([]);
  const [creditorInput, setCreditorInput] = useState(editing?.alacakli_adi || "");
  const [recentCreditors, setRecentCreditors] = useState([]);
  const [favoriteCreditors, setFavoriteCreditors] = useState([]);
  const [history, setHistory] = useState([]);
  const [histDup, setHistDup] = useState(null); // {foy_count, esas_count, foy_matches, esas_matches}
  const [histDupLoading, setHistDupLoading] = useState(false);
  const [quickViewId, setQuickViewId] = useState(null);
  // When true, user explicitly overrode the auto-detected "Varlık Şirketi" value
  // so the checkbox is editable even though the müvekkil name contains "varlık yönetim".
  const [varlikOverride, setVarlikOverride] = useState(false);
  // Admin-defined müvekkil → flag rules (loaded once; applied live as the user
  // types alacakli_adi). Default seed covers "varlık yönetim" → varlik_sirketi.
  const [flagRules, setFlagRules] = useState([]);
  // Admin-configurable "Giden Kişiler" position scope (restricts personel role).
  // Falls back to [Haciz Birimi, Call Center] when the backend has no override.
  const [gidenScope, setGidenScope] = useState(["Haciz Birimi", "Call Center"]);
  const isEditing = !!editing?.id;
  const canSeeHistory = !isPersonel && isEditing;
  const canSeeHistDup = !isPersonel; // admin + muhasebeci only

  // Load creditors list + recents + favorites + prim history (edit mode)
  useEffect(() => {
    api.get("/creditors").then((r) => setCreditors(r.data?.rows || [])).catch(() => setCreditors([]));
    api.get("/creditors/recent?limit=5")
      .then((r) => setRecentCreditors(r.data?.rows || []))
      .catch(() => setRecentCreditors([]));
    api.get("/me/favorite-creditors")
      .then((r) => setFavoriteCreditors(r.data?.rows || []))
      .catch(() => setFavoriteCreditors([]));
    api.get("/creditor-flag-rules")
      .then((r) => setFlagRules((r.data?.rows || []).filter((x) => x.is_active)))
      .catch(() => setFlagRules([]));
    api.get("/prim/giden-scope-positions")
      .then((r) => {
        const list = r.data?.positions;
        if (Array.isArray(list) && list.length) setGidenScope(list);
      })
      .catch(() => { /* keep default fallback */ });
    if (canSeeHistory) {
      api.get(`/prim/${editing.id}/history`)
        .then((r) => setHistory(r.data?.events || []))
        .catch(() => setHistory([]));
    }
  }, [canSeeHistory, editing?.id]);

  // Historical duplicate check (admin + muhasebeci only) — debounced 400ms
  useEffect(() => {
    if (!canSeeHistDup) return;
    const foy = (form.icra_foy || "").trim();
    const esas = (form.icra_esas || "").trim();
    if (!foy && !esas) { setHistDup(null); return; }
    setHistDupLoading(true);
    const handle = setTimeout(() => {
      const params = new URLSearchParams();
      if (foy) params.set("icra_foy", foy);
      if (esas) params.set("icra_esas", esas);
      if (editing?.id) params.set("exclude_id", editing.id);
      api.get(`/prim/historical-dup-check?${params.toString()}`)
        .then((r) => setHistDup(r.data || null))
        .catch(() => setHistDup(null))
        .finally(() => setHistDupLoading(false));
    }, 400);
    return () => { clearTimeout(handle); setHistDupLoading(false); };
  }, [form.icra_foy, form.icra_esas, canSeeHistDup, editing?.id]);

  const pickRecentCreditor = (ad) => {
    setForm((f) => ({ ...f, alacakli_adi: ad }));
    setCreditorInput(ad);
  };

  // Merge favorites first, then recent (dedup by lowercase name)
  const quickChips = (() => {
    const seen = new Set();
    const chips = [];
    for (const fc of favoriteCreditors) {
      const key = (fc.ad || "").toLocaleLowerCase("tr");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      chips.push({ ad: fc.ad, favorite: true });
    }
    for (const rc of recentCreditors) {
      const key = (rc.ad || "").toLocaleLowerCase("tr");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      chips.push({ ad: rc.ad, favorite: false, last_tarih: rc.last_tarih });
    }
    return chips.slice(0, 8);
  })();

  // Auto-set oran based on (varlık, şehir dışı) matrix whenever either toggles
  useEffect(() => {
    if (!canSeePrimRate) return;
    setForm((f) => ({ ...f, oran: autoRate(f.varlik_sirketi, f.sehir_disi) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.varlik_sirketi, form.sehir_disi, canSeePrimRate]);

  // Auto-detect flag values from admin-defined müvekkil rules. Rules are applied
  // live as the user types/picks the müvekkil adı: first active matching rule per
  // flag_key wins (rules are already sorted by priority asc on the backend). When
  // a rule hits `varlik_sirketi`, the checkbox is locked and labelled "OTO ·
  // KİLİTLİ"; "Elle Değiştir" clears the lock for that session. Rules with
  // flag_key="tag" accumulate into form.tags[] as deduped chips.
  const flagMatches = React.useMemo(() => {
    const name = form.alacakli_adi || "";
    if (!name || !flagRules.length) return { tags: [] };
    const out = { tags: [] };
    const seenTags = new Set();
    for (const rule of flagRules) {
      try {
        const rx = new RegExp(rule.pattern, "i");
        if (!rx.test(name)) continue;
        if (rule.flag_key === "tag") {
          const t = (rule.tag || "").trim();
          if (t && !seenTags.has(t.toLocaleLowerCase("tr"))) {
            seenTags.add(t.toLocaleLowerCase("tr"));
            out.tags.push({ value: t, rule });
          }
        } else if (!out[rule.flag_key]) {
          out[rule.flag_key] = { value: !!rule.flag_value, rule };
        }
      } catch (_) { /* ignore invalid pattern on client */ }
    }
    return out;
  }, [form.alacakli_adi, flagRules]);
  const varlikRuleHit = flagMatches.varlik_sirketi || null;
  const sehirDisiRuleHit = flagMatches.sehir_disi || null;
  const autoTagHits = flagMatches.tags || [];
  const autoTagValues = autoTagHits.map((t) => t.value);
  const varlikAutoMatch = !!varlikRuleHit;
  const varlikLocked = varlikAutoMatch && !varlikOverride;

  // Apply flag rules whenever the müvekkil adı changes (unless user is overriding
  // that specific flag). Tag rules append their value to form.tags (deduped,
  // Turkish lowercase match) and never remove manual entries.
  useEffect(() => {
    setForm((f) => {
      let next = f;
      if (varlikRuleHit && !varlikOverride && f.varlik_sirketi !== varlikRuleHit.value) {
        next = { ...next, varlik_sirketi: varlikRuleHit.value };
      }
      if (sehirDisiRuleHit && f.sehir_disi !== sehirDisiRuleHit.value) {
        next = { ...next, sehir_disi: sehirDisiRuleHit.value };
      }
      if (autoTagValues.length) {
        const current = Array.isArray(next.tags) ? next.tags : [];
        const lower = new Set(current.map((t) => (t || "").toLocaleLowerCase("tr")));
        const additions = autoTagValues.filter((t) => !lower.has((t || "").toLocaleLowerCase("tr")));
        if (additions.length) next = { ...next, tags: [...current, ...additions] };
      }
      return next;
    });
    // autoTagValues is derived from flagMatches.tags; depending on the array
    // itself keeps the effect stable across unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varlikRuleHit, sehirDisiRuleHit, varlikOverride, autoTagValues.join("|")]);
  // Reset override whenever müvekkil adı changes (fresh name → fresh auto)
  useEffect(() => {
    setVarlikOverride(false);
  }, [form.alacakli_adi]);

  // Dropdown options: show active personnel + any already-chosen inactive ones (for edit mode continuity)
  const chosenIds = new Set([
    ...(form.giden_kisiler || []).filter(Boolean),
    form.arastiran_kisi_id,
  ].filter(Boolean));
  const selectablePersonnel = (personnel || []).filter(
    (p) => p.aktif !== false || chosenIds.has(p.id)
  );
  // Giden Kişiler scope: for "personel" role, restrict to the admin-configured
  // position list (default [Haciz Birimi, Call Center]) plus self and any
  // already-chosen row so edit mode stays valid. Admin / muhasebeci see every
  // active personnel like before.
  const gidenScopeNorm = React.useMemo(
    () => new Set(gidenScope.map((s) => (s || "").trim().toLocaleLowerCase("tr"))),
    [gidenScope]
  );
  const isInGidenScope = (poz) => {
    const n = (poz || "").trim().toLocaleLowerCase("tr");
    return gidenScopeNorm.has(n);
  };
  const gidenSelectablePersonnel = (isPersonel || isMuhasebeci)
    ? selectablePersonnel.filter(
        (p) =>
          (myPersonnelId && p.id === myPersonnelId) ||
          chosenIds.has(p.id) ||
          isInGidenScope(p.pozisyon)
      )
    : selectablePersonnel;
  const personelLabel = (p) =>
    `${p.ad_soyad}${p.sicil_no ? ` · ${p.sicil_no}` : ""}${p.aktif === false ? " (pasif)" : ""}`;

  const addCreditorInline = async () => {
    const ad = (creditorInput || "").trim();
    if (!ad) { toast.error("Önce müvekkil adını yazın"); return; }
    try {
      const r = await api.post("/creditors", { ad });
      const row = r.data;
      setCreditors((prev) => (prev.find((c) => c.id === row.id) ? prev : [...prev, row].sort((a, b) => a.ad.localeCompare(b.ad, "tr"))));
      setForm((f) => ({ ...f, alacakli_adi: row.ad }));
      setCreditorInput(row.ad);
      toast.success("Müvekkil listeye eklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Müvekkil eklenemedi");
    }
  };

  const num = (v) => Number(v || 0);
  const oran = num(form.oran) / 100;
  const odenecek_toplam = form.varlik_sirketi
    ? num(form.tahsilat_miktari) * oran
    : num(form.vekalet_ucreti) * oran;
  const giden_count = form.giden_kisiler.filter(Boolean).length;
  const kisi_basi = giden_count > 0 ? odenecek_toplam / giden_count : 0;
  const arastirma_primi = form.arastiran_kisi_id
    ? (num(form.tahsilat_miktari) / 1_000_000) * 1500
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tarih) {
      toast.error("Tarih zorunludur");
      return;
    }
    // Personel-side required-field validation (matches backend)
    if (isPersonel) {
      const missing = [];
      if (!(form.icra_foy || "").trim())    missing.push("İcra Föy");
      if (!(form.icra_esas || "").trim())   missing.push("İcra Esas");
      if (!(form.dosya_adi || "").trim())   missing.push("Dosya Adı");
      if (!(form.alacakli_adi || "").trim()) missing.push("Müvekkil");
      if (missing.length > 0) {
        toast.error(`Zorunlu alanları doldurun: ${missing.join(", ")}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        tarih: form.tarih,
        icra_foy: form.icra_foy || "",
        icra_esas: form.icra_esas || "",
        icra_dairesi: form.icra_dairesi || "",
        uyap_url: form.uyap_url || "",
        dosya_adi: form.dosya_adi || "",
        alacakli_adi: form.alacakli_adi || "",
        varlik_sirketi: !!form.varlik_sirketi,
        sehir_disi: !!form.sehir_disi,
        oran: num(form.oran),
        vekalet_ucreti: num(form.vekalet_ucreti),
        tahsilat_miktari: num(form.tahsilat_miktari),
        giden_kisiler: form.giden_kisiler.filter(Boolean),
        arastiran_kisi_id: form.arastiran_kisi_id || null,
        aciklama: form.aciklama || "",
        tags: Array.isArray(form.tags) ? form.tags.filter(Boolean) : [],
        prim_ayi: form.prim_ayi || null,
      };
      if (editing) {
        await api.put(`/prim/${editing.id}`, payload);
        toast.success("Prim güncellendi");
      } else {
        await api.post("/prim", payload);
        toast.success("Prim kaydedildi");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const updateGiden = (idx, val) => {
    const next = [...form.giden_kisiler];
    next[idx] = val;
    setForm({ ...form, giden_kisiler: next });
  };

  const chevronBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`;
  const selectCls = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 cursor-pointer appearance-none bg-[length:1.25rem] bg-no-repeat bg-[right_0.75rem_center] pr-9";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl my-8" data-testid="prim-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Scale size={18} /> {editing ? "Prim Düzenle" : "Yeni Prim Kaydı"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {canSeeHistory && history.length > 0 && (
            <PrimHistoryTimeline events={history} />
          )}

          {isAdmin && isEditing && editing?.onaylandi && (
            <VisibilityCountdown
              primId={editing.id}
              onayISO={editing.onay_tarihi}
              personelVisibility={editing.personel_visibility}
              extendedISO={form.visibility_extended_until || editing.visibility_extended_until}
              onExtended={(iso) => {
                setForm((f) => ({ ...f, visibility_extended_until: iso }));
              }}
            />
          )}

          <div className="grid grid-cols-3 gap-4">
            <Field label="Tarih *">
              <DateInput data-testid="prim-tarih" required value={form.tarih} onChange={(e) => setForm({ ...form, tarih: e.target.value })} className="input" />
            </Field>
            <Field label="Prim Ayı *">
              <PrimMonthPicker value={primPeriod(form)} onChange={(v) => setForm({ ...form, prim_ayi: v })} />
            </Field>
            <Field label={isPersonel ? "İcra Föy *" : "İcra Föy"}>
              <input
                data-testid="prim-icra-foy"
                value={form.icra_foy}
                onChange={(e) => setForm({ ...form, icra_foy: e.target.value })}
                required={isPersonel}
                className={`input ${isPersonel && !form.icra_foy ? "ring-1 ring-amber-300" : ""}`}
              />
            </Field>
            <Field label={isPersonel ? "İcra Esas *" : "İcra Esas"}>
              <input
                data-testid="prim-icra-esas"
                value={form.icra_esas}
                onChange={(e) => setForm({ ...form, icra_esas: e.target.value })}
                required={isPersonel}
                className={`input ${isPersonel && !form.icra_esas ? "ring-1 ring-amber-300" : ""}`}
              />
            </Field>
          </div>

          {canSeeHistDup && histDup && (histDup.foy_count > 0 || histDup.esas_count > 0) && (
            <HistoricalDupWarning data={histDup} onOpenPrim={(id) => setQuickViewId(id)} />
          )}
          {canSeeHistDup && histDupLoading && (form.icra_foy || form.icra_esas) && !histDup && (
            <div className="text-[11px] text-slate-400 italic -mt-2" data-testid="hist-dup-loading">
              Önceki aylar kontrol ediliyor...
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label={isPersonel ? "Dosya Adı *" : "Dosya Adı"}>
              <input
                data-testid="prim-dosya-adi"
                value={form.dosya_adi}
                onChange={(e) => setForm({ ...form, dosya_adi: e.target.value })}
                required={isPersonel}
                className={`input ${isPersonel && !form.dosya_adi ? "ring-1 ring-amber-300" : ""}`}
              />
            </Field>
            <Field label={isPersonel ? "Müvekkil Adı *" : "Müvekkil Adı"}>
              {quickChips.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2" data-testid="recent-creditors-chips">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 self-center mr-0.5">Hızlı:</span>
                  {quickChips.map((rc) => {
                    const isActive = form.alacakli_adi === rc.ad;
                    const cls = rc.favorite
                      ? (isActive
                          ? "bg-amber-500 text-white border-amber-500"
                          : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200")
                      : (isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200");
                    return (
                      <button
                        key={rc.ad}
                        type="button"
                        data-testid={`recent-creditor-chip-${rc.ad}`}
                        onClick={() => pickRecentCreditor(rc.ad)}
                        title={rc.favorite ? "★ Favori müvekkil" : `Son kullanım: ${rc.last_tarih || "-"}`}
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors inline-flex items-center gap-1 ${cls}`}
                      >
                        {rc.favorite && <span className="leading-none">★</span>}
                        {rc.ad.length > 24 ? rc.ad.slice(0, 24) + "…" : rc.ad}
                      </button>
                    );
                  })}
                </div>
              )}
              {isPersonel ? (
                <select
                  data-testid="prim-alacakli-select"
                  value={form.alacakli_adi}
                  onChange={(e) => setForm({ ...form, alacakli_adi: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">— Listeden seçin —</option>
                  {creditors.map((c) => (
                    <option key={c.id} value={c.ad}>{c.ad}</option>
                  ))}
                </select>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="prim-alacakli-input"
                    list="creditor-datalist"
                    value={creditorInput}
                    onChange={(e) => { setCreditorInput(e.target.value); setForm({ ...form, alacakli_adi: e.target.value }); }}
                    placeholder="Yaz ya da listeden seç"
                    className="input flex-1"
                  />
                  <datalist id="creditor-datalist">
                    {creditors.map((c) => <option key={c.id} value={c.ad} />)}
                  </datalist>
                  {canAddCreditor && (
                    <button
                      type="button"
                      data-testid="add-creditor-button"
                      onClick={addCreditorInline}
                      title="Müvekkili listeye kaydet"
                      className="shrink-0 inline-flex items-center justify-center w-10 h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                    >
                      <Plus size={14} />
                    </button>
                  )}
                </div>
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <Field label="İcra Dairesi">
              <input
                data-testid="prim-icra-dairesi"
                value={form.icra_dairesi}
                onChange={(e) => setForm({ ...form, icra_dairesi: e.target.value })}
                placeholder="Örn: İstanbul 5. İcra Dairesi"
                className="input"
              />
            </Field>
            <Field label="UYAP Dosya Linki">
              <input
                data-testid="prim-uyap-url"
                type="url"
                value={form.uyap_url}
                onChange={(e) => setForm({ ...form, uyap_url: e.target.value })}
                placeholder="https://vatandas.uyap.gov.tr/..."
                className="input"
              />
            </Field>
          </div>

          <TagsRow
            tags={form.tags || []}
            autoTags={autoTagValues}
            onChange={(tags) => setForm({ ...form, tags })}
            readOnly={isPersonel}
          />

          <div className="flex flex-wrap items-center gap-3">
            <label className={`flex items-center gap-2 text-sm font-semibold ${varlikLocked ? "cursor-not-allowed opacity-90" : "cursor-pointer"} bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2`}>
              <input
                data-testid="prim-varlik-checkbox"
                type="checkbox"
                checked={form.varlik_sirketi}
                disabled={!canSeePrimRate || varlikLocked}
                onChange={(e) => setForm({ ...form, varlik_sirketi: e.target.checked })}
              />
              Müvekkil Varlık Şirketi mi?
              <span className="text-xs text-indigo-700 font-normal">
                {canSeePrimRate ? (form.varlik_sirketi ? "(Tahsilat × Oran)" : "(Vekalet × Oran)") : "(Yalnızca yönetici)"}
              </span>
              {varlikLocked && (
                <span
                  data-testid="prim-varlik-auto-badge"
                  title={varlikRuleHit?.rule?.name ? `Kural: ${varlikRuleHit.rule.name} · pattern: ${varlikRuleHit.rule.pattern}` : 'Müvekkil adı "varlık yönetim" içerdiği için otomatik işaretlendi'}
                  className="text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white rounded px-1.5 py-0.5"
                >
                  Oto · Kilitli
                </span>
              )}
            </label>
            {varlikAutoMatch && canSeePrimRate && (
              <button
                type="button"
                data-testid="prim-varlik-override-btn"
                onClick={() => setVarlikOverride((v) => !v)}
                className={`text-xs font-semibold px-3 py-2 rounded-lg border transition ${
                  varlikOverride
                    ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                    : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                }`}
                title={varlikOverride ? "Otomatik algılamayı yeniden aktif et" : "Otomatik kilidi aç ve manuel değiştir"}
              >
                {varlikOverride ? "Oto Algılamayı Kullan" : "Elle Değiştir"}
              </button>
            )}

            <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="prim-sehir-toggle">
              <button
                type="button"
                data-testid="prim-sehir-ici"
                onClick={() => setForm({ ...form, sehir_disi: false })}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors ${
                  !form.sehir_disi ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
                aria-pressed={!form.sehir_disi}
              >
                <Building2 size={14} /> Şehir İçi
              </button>
              <button
                type="button"
                data-testid="prim-sehir-disi"
                onClick={() => setForm({ ...form, sehir_disi: true })}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-l border-slate-200 transition-colors ${
                  form.sehir_disi ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
                aria-pressed={form.sehir_disi}
              >
                <Car size={14} /> Şehir Dışı
              </button>
            </div>

            {canSeePrimRate && (
              <span className="text-xs font-mono-tab bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-2 py-1" data-testid="prim-oto-oran-hint">
                Oto-oran: <strong>%{autoRate(form.varlik_sirketi, form.sehir_disi).toString().replace(".", ",")}</strong>
              </span>
            )}
          </div>

          <div className={`grid gap-4 ${canSeePrimRate ? "grid-cols-3" : "grid-cols-2"}`}>
            {canSeePrimRate && (
              <Field label="Oran (%) *">
                <input data-testid="prim-oran" type="number" step="0.01" value={form.oran} onChange={(e) => setForm({ ...form, oran: e.target.value })} className="input font-mono-tab" />
              </Field>
            )}
            <Field label="Vekalet Ücreti (₺)">
              <input data-testid="prim-vekalet" type="number" step="0.01" value={form.vekalet_ucreti} onChange={(e) => setForm({ ...form, vekalet_ucreti: e.target.value })} className="input font-mono-tab" />
            </Field>
            <Field label="Tahsilat Miktarı (₺)">
              <input data-testid="prim-tahsilat" type="number" step="0.01" value={form.tahsilat_miktari} onChange={(e) => setForm({ ...form, tahsilat_miktari: e.target.value })} className="input font-mono-tab" />
            </Field>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Giden Personel (En fazla 4)
              {isPersonel && myPersonnelId && (
                <span className="ml-2 text-[10px] font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  1. Giden otomatik: siz · diğer gidenler eklenebilir
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.giden_kisiler.map((val, i) => {
                const isSelfSlot = isPersonel && i === 0 && !!myPersonnelId;
                return (
                  <select
                    key={i}
                    data-testid={`prim-giden-${i}`}
                    value={val}
                    onChange={(e) => updateGiden(i, e.target.value)}
                    disabled={isSelfSlot}
                    className={`${selectCls} ${isSelfSlot ? "bg-emerald-50 cursor-not-allowed" : ""}`}
                    style={{ backgroundImage: chevronBg }}
                    title={isSelfSlot ? "1. giden otomatik olarak sizsiniz; diğer slotları seçebilirsiniz" : undefined}
                  >
                    <option value="">— Giden Kişi {i + 1} —</option>
                    {gidenSelectablePersonnel.length === 0 && (
                      <option value="" disabled>— Personel yükleniyor —</option>
                    )}
                    {gidenSelectablePersonnel.map((p) => (
                      <option key={p.id} value={p.id} data-testid={`prim-giden-${i}-option-${p.id}`}>
                        {personelLabel(p)}
                      </option>
                    ))}
                  </select>
                );
              })}
            </div>
            {gidenSelectablePersonnel.length === 0 && (
              <p className="text-[11px] text-amber-700 mt-1.5" data-testid="prim-personel-empty-hint">
                Personel listesi yüklenemedi veya boş. Personel sekmesinden kayıt ekleyin.
              </p>
            )}
            {(isPersonel || isMuhasebeci) && (
              <p className="text-[10px] text-slate-500 mt-1.5">
                Giden olarak yalnızca şu pozisyonlardaki personel seçilebilir: <strong>{gidenScope.join(", ")}</strong>
              </p>
            )}
          </div>

          {!isPersonel && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <Field label="Araştıran Kişi">
                <select
                  data-testid="prim-arastiran"
                  value={form.arastiran_kisi_id}
                  onChange={(e) => setForm({ ...form, arastiran_kisi_id: e.target.value })}
                  className={selectCls}
                  style={{ backgroundImage: chevronBg }}
                >
                  <option value="">— Seçin —</option>
                  {selectablePersonnel.length === 0 && (
                    <option value="" disabled>— Personel yükleniyor —</option>
                  )}
                  {selectablePersonnel.map((p) => (
                    <option key={p.id} value={p.id} data-testid={`prim-arastiran-option-${p.id}`}>
                      {personelLabel(p)}
                    </option>
                  ))}
                </select>
              </Field>
              <p className="text-[11px] text-slate-500 mt-2">
                Araştırma primi, <strong>Tahsilat Miktarı</strong> üzerinden hesaplanır: her 1.000.000 ₺ için 1.500 ₺.
              </p>
            </div>
          )}

          {!isPersonel && (
            <Field label="Açıklama">
              <input value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} className="input" placeholder="Opsiyonel" />
            </Field>
          )}

          {canSeePrimRate && (
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
              <PreviewCard label="Ödenecek Toplam" value={formatTL(odenecek_toplam)} tone="emerald" />
              <PreviewCard label={`Kişi Başı (${giden_count} kişi)`} value={formatTL(kisi_basi)} tone="emerald" />
              <PreviewCard label="Araştırma Primi" value={formatTL(arastirma_primi)} tone="indigo" />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
            <button data-testid="save-prim-button" disabled={saving} className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input { width: 100%; padding: 8px 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; background: #fff; } .input:focus { border-color: #0F766E; box-shadow: 0 0 0 3px rgba(15,118,110,0.15); }`}</style>
      {quickViewId && (
        <PrimQuickViewModalInline primId={quickViewId} onClose={() => setQuickViewId(null)} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PrimQuickViewModalInline({ primId, onClose }) {
  const [prim, setPrim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/prim/${primId}`)
      .then((r) => { if (active) setPrim(r.data); })
      .catch((e) => { if (active) setErr(e?.response?.data?.detail || "Prim yüklenemedi"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [primId]);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || "—"; } };
  const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 });
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="prim-quickview-modal-inline">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2 text-indigo-800">
              <Eye size={18} /> Prim Önizlemesi
              {prim?.onaylandi && <span className="inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Onaylı</span>}
              {prim && !prim.onaylandi && <span className="inline-flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-900 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Bekleyen</span>}
              {prim?.onay_override && (
                <span
                  className="inline-flex items-center gap-1 bg-red-100 border border-red-400 text-red-900 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                  title={`Admin OVERRIDE · ${prim.onay_override_blockers?.length || 0} mükerrer föy bypass edildi`}
                >
                  ⚠ OVERRIDE
                </span>
              )}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5 font-mono-tab">{primId}</p>
          </div>
          <button data-testid="prim-quickview-inline-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading && <div className="text-center text-sm text-slate-500 py-6">Yükleniyor...</div>}
          {err && <div className="text-center text-sm text-red-700 py-6">{err}</div>}
          {prim && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tarih</div><div className="text-sm font-semibold text-slate-900">{prim.tarih ? prim.tarih.slice(0, 10).split("-").reverse().join(".") : "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">İcra Föy</div><div className="text-sm font-semibold text-slate-900">{prim.icra_foy || "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">İcra Esas</div><div className="text-sm font-semibold text-slate-900">{prim.icra_esas || "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Oran</div><div className="text-sm font-semibold text-slate-900">%{prim.oran || 0}</div></div>
                <div className="col-span-2"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dosya Adı</div><div className="text-sm font-semibold text-slate-900 truncate">{prim.dosya_adi || "—"}</div></div>
                <div className="col-span-2"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Müvekkil</div><div className="text-sm font-semibold text-slate-900 truncate">{prim.alacakli_adi || "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tahsilat</div><div className="text-sm font-semibold text-slate-900">{fmtMoney(prim.tahsilat_miktari)} ₺</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vekalet</div><div className="text-sm font-semibold text-slate-900">{fmtMoney(prim.vekalet_ucreti)} ₺</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Prim Payı</div><div className="text-sm font-semibold text-slate-900">{fmtMoney(prim.prim_payi)} ₺</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Varlık Şirketi</div><div className="text-sm font-semibold text-slate-900">{prim.varlik_sirketi ? "Evet" : "Hayır"}</div></div>
              </div>
              {prim.giden_isimleri?.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Giden Kişiler</div>
                  <div className="flex flex-wrap gap-1">
                    {prim.giden_isimleri.map((n, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2 py-0.5 text-xs font-semibold text-slate-700">{n}</span>
                    ))}
                  </div>
                </div>
              )}
              {prim.onaylandi && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-xs">
                  <div className="font-bold text-emerald-900">Onay Bilgileri</div>
                  <div className="mt-1 text-emerald-800"><strong>Onaylayan:</strong> {prim.onaylayan_email || "—"}</div>
                  <div className="text-emerald-800"><strong>Onay Tarihi:</strong> {fmtDate(prim.onay_tarihi)}</div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Kapat</button>
        </div>
      </div>
    </div>
  );
}

function HistoricalDupWarning({ data, onOpenPrim }) {
  const foy = data?.foy_matches || [];
  const esas = data?.esas_matches || [];
  const total = (data?.foy_count || 0) + (data?.esas_count || 0);
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso || "—"; }
  };
  const Chip = ({ m }) => (
    <button
      type="button"
      data-testid={`hist-dup-chip-${m.id}`}
      onClick={() => onOpenPrim && onOpenPrim(m.id)}
      className="inline-flex items-center gap-1 bg-white border border-amber-200 hover:bg-amber-100 hover:border-amber-400 rounded px-1.5 py-0.5 font-mono-tab transition-colors"
      title={`Detay için tıkla · ${m.dosya_adi || "—"} · ${m.alacakli_adi || "—"}`}
    >
      {fmt(m.tarih)}
      <span className="opacity-60">·</span>
      <span className="truncate max-w-[140px]">{m.dosya_adi || "—"}</span>
      {m.onaylandi
        ? <span className="text-[9px] px-1 bg-emerald-100 text-emerald-700 rounded font-semibold">Onaylı</span>
        : <span className="text-[9px] px-1 bg-slate-100 text-slate-600 rounded font-semibold">Bekleyen</span>}
    </button>
  );
  return (
    <div
      data-testid="hist-dup-warning"
      className="bg-amber-50 border border-amber-300 rounded-lg p-3 -mt-2 shadow-sm"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
            Önceki aylarda {total} kez kullanılmış · onaylamadan önce kontrol edin
          </div>
          {foy.length > 0 && (
            <div className="text-xs text-amber-900 flex flex-wrap gap-1 items-center">
              <span className="font-bold">İcra Föy ({data.foy_count}):</span>
              {foy.map((m) => <Chip key={m.id} m={m} />)}
            </div>
          )}
          {esas.length > 0 && (
            <div className="text-xs text-amber-900 flex flex-wrap gap-1 items-center">
              <span className="font-bold">İcra Esas ({data.esas_count}):</span>
              {esas.map((m) => <Chip key={m.id} m={m} />)}
            </div>
          )}
          <div className="text-[10px] text-amber-700 italic">
            Chip'e tıklayarak önceki primin detayını açabilirsiniz · Mükerrer kayıt olabilir — gerekirse birleştirin veya reddedin.
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewCard({ label, value, tone }) {
  const toneCls = tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : tone === "indigo" ? "bg-indigo-50 border-indigo-200 text-indigo-800" : "bg-slate-50 border-slate-200";
  return (
    <div className={`rounded-lg p-3 border ${toneCls}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-mono-tab font-bold text-lg mt-1">{value}</div>
    </div>
  );
}

function PrimHistoryTimeline({ events }) {
  const CATEGORIES = [
    { key: "created",         label: "Oluşturma", dot: "bg-indigo-500", chipOn: "bg-indigo-600 text-white border-indigo-600", chipOff: "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50" },
    { key: "pre_approved",    label: "Ön Onay",   dot: "bg-sky-500",    chipOn: "bg-sky-600 text-white border-sky-600",       chipOff: "bg-white text-sky-700 border-sky-200 hover:bg-sky-50" },
    { key: "pre_unapproved",  label: "Ön Geri",   dot: "bg-sky-300",    chipOn: "bg-sky-400 text-white border-sky-400",       chipOff: "bg-white text-sky-600 border-sky-100 hover:bg-sky-50" },
    { key: "approved",        label: "Onay",      dot: "bg-emerald-500", chipOn: "bg-emerald-600 text-white border-emerald-600", chipOff: "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50" },
    { key: "unapproved",      label: "Onay Geri", dot: "bg-amber-500",   chipOn: "bg-amber-600 text-white border-amber-600",   chipOff: "bg-white text-amber-700 border-amber-200 hover:bg-amber-50" },
    { key: "rejected",        label: "Reddet",    dot: "bg-rose-500",    chipOn: "bg-rose-600 text-white border-rose-600",     chipOff: "bg-white text-rose-700 border-rose-200 hover:bg-rose-50" },
    { key: "tahsilat_fill",   label: "Tahsilattan Doldurma", dot: "bg-violet-500", chipOn: "bg-violet-600 text-white border-violet-600", chipOff: "bg-white text-violet-700 border-violet-200 hover:bg-violet-50" },
    { key: "edited",          label: "Düzenleme", dot: "bg-slate-500",   chipOn: "bg-slate-700 text-white border-slate-700",   chipOff: "bg-white text-slate-700 border-slate-200 hover:bg-slate-50" },
  ];
  const META = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
  // All on by default
  const [active, setActive] = useState(() => new Set(CATEGORIES.map((c) => c.key)));
  const toggle = (key) => setActive((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const inferCategory = (e) => {
    if (e.category) return e.category;
    // Backward compat: infer from type/label (older cached responses)
    const lbl = (e.label || "").toLowerCase();
    if (e.type === "create") return "created";
    if (e.type === "delete") return "rejected";
    if (lbl.includes("muhasebeci ön onayı geri")) return "pre_unapproved";
    if (lbl.includes("muhasebeci ön onayı")) return "pre_approved";
    if (lbl.includes("onay geri")) return "unapproved";
    if (lbl.includes("onayland") || lbl.includes("toplu onay")) return "approved";
    return "edited";
  };

  const filtered = events.filter((e) => active.has(inferCategory(e)));
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16); }
  };
  const roleLabel = (r) => r === "admin" ? "Admin" : r === "yonetici" ? "Yönetici" : r === "muhasebeci" ? "Muhasebeci" : r === "personel" ? "Personel" : (r || "—");
  // Count per category for chip badges
  const counts = events.reduce((acc, e) => {
    const c = inferCategory(e);
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  return (
    <details
      data-testid="prim-history-timeline"
      className="bg-slate-50 border border-slate-200 rounded-lg"
      open={events.length <= 3}
    >
      <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center justify-between select-none">
        <span>Onay Geçmişi · {filtered.length}/{events.length} olay</span>
        <span className="text-[10px] text-slate-400">▼</span>
      </summary>
      <div className="px-3 pt-2 pb-1 border-t border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-1" data-testid="prim-history-filter-chips">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Filtre:</span>
          {CATEGORIES.map((c) => {
            const on = active.has(c.key);
            const n = counts[c.key] || 0;
            return (
              <button
                key={c.key}
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggle(c.key); }}
                data-testid={`prim-history-filter-${c.key}`}
                aria-pressed={on}
                title={`${c.label} olaylarını ${on ? "gizle" : "göster"} (${n})`}
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors inline-flex items-center gap-1 ${on ? c.chipOn : c.chipOff}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
                {c.label}
                <span className={`ml-0.5 ${on ? "opacity-80" : "opacity-50"}`}>({n})</span>
              </button>
            );
          })}
        </div>
      </div>
      <ol className="px-4 pb-3 pt-2 space-y-2 bg-white">
        {filtered.length === 0 ? (
          <li className="text-xs text-slate-400 italic py-2" data-testid="prim-history-empty">
            Seçilen filtrelere uygun olay yok.
          </li>
        ) : filtered.map((e, i) => {
          const cat = inferCategory(e);
          const meta = META[cat] || { label: cat, dot: "bg-slate-400" };
          return (
            <li key={i} className="relative pl-5 text-xs" data-testid={`prim-history-event-${i}`}>
              <span className={`absolute left-0 top-1 w-2.5 h-2.5 rounded-full ring-2 ring-white ${meta.dot}`}></span>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-bold text-slate-800">{meta.label}</span>
                <span className="text-slate-400 font-mono-tab">·</span>
                <span className="text-slate-600 font-mono-tab">{fmt(e.at)}</span>
                {e.is_impersonated && (
                  <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5">
                    Impersonate
                  </span>
                )}
              </div>
              <div className="text-slate-600 mt-0.5">
                <span className="font-semibold">{e.actor_email || "—"}</span>
                {e.actor_role && <span className="opacity-60"> ({roleLabel(e.actor_role)})</span>}
                {e.label && <span className="opacity-70"> · {e.label}</span>}
              </div>
              {(e.changes || []).length > 0 && (
                <ul className="mt-0.5 text-[11px] text-violet-800" data-testid={`prim-history-changes-${i}`}>
                  {e.changes.map((c, j) => (
                    <li key={j}>
                      <span className="font-semibold">{c.field}:</span>{" "}
                      <span className="line-through opacity-60">{typeof c.before === "number" ? formatNum(c.before) : (c.before ?? "—")}</span>
                      {" → "}
                      <span className="font-semibold">{typeof c.after === "number" ? formatNum(c.after) : (c.after ?? "—")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </details>
  );
}

// =========================
// VisibilityCountdown — Admin görünürlük zamanlayıcısı (approved + personel kuralları)
// =========================
function VisibilityCountdown({ primId, onayISO, personelVisibility, extendedISO, onExtended }) {
  const [busy, setBusy] = useState(false);
  const vis = personelVisibility; // "forced_visible" | "forced_hidden" | null
  const extISO  = extendedISO;

  const now = new Date();
  // Default hide time = onay + 7d, unless extended beyond that
  let hideAt = null;
  if (onayISO) {
    try {
      const o = new Date(onayISO);
      const base = new Date(o.getTime() + 7 * 24 * 60 * 60 * 1000);
      if (extISO) {
        const e = new Date(extISO);
        hideAt = (e.getTime() > base.getTime()) ? e : base;
      } else {
        hideAt = base;
      }
    } catch { /* ignore */ }
  }

  let state = "default";
  let message = "";
  if (vis === "forced_hidden") {
    state = "hidden";
    message = "Personel'den gizlenmiş · yönetici 'default' ile açabilir";
  } else if (vis === "forced_visible") {
    state = "always";
    message = "Personel kalıcı olarak görüyor · yönetici 'default' ile 7 gün kuralına döner";
  } else if (hideAt) {
    const diffMs = hideAt - now;
    if (diffMs <= 0) {
      state = "expired";
      message = "Görünürlük süresi doldu · personel artık görmüyor";
    } else {
      const dDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
      const dHours = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      state = "counting";
      const fmt = hideAt.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
      message = `${dDays} gün ${dHours} saat sonra (${fmt}) personel görünürlüğü sona erer`;
    }
  }

  const extend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/prim/${primId}/extend-visibility`, { days: 7 });
      toast.success(`+7 gün uzatıldı · yeni bitiş: ${new Date(r.data.visibility_extended_until).toLocaleDateString("tr-TR")}`);
      onExtended && onExtended(r.data.visibility_extended_until);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uzatma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const stateCls = {
    counting: "bg-sky-50 border-sky-200 text-sky-900",
    expired:  "bg-slate-100 border-slate-200 text-slate-700",
    hidden:   "bg-rose-50 border-rose-200 text-rose-800",
    always:   "bg-emerald-50 border-emerald-200 text-emerald-900",
    default:  "bg-slate-50 border-slate-200 text-slate-700",
  }[state];
  const StateIcon = state === "hidden" ? EyeOffIcon : state === "always" ? Eye : ClockIcon;

  return (
    <div
      className={`rounded-lg border px-3 py-2 text-xs flex flex-wrap items-center justify-between gap-2 ${stateCls}`}
      data-testid="prim-visibility-countdown"
    >
      <div className="flex items-center gap-2 min-w-0">
        <StateIcon size={14} className="shrink-0" />
        <span className="truncate" data-testid="visibility-countdown-text">{message || "Görünürlük verisi yok"}</span>
      </div>
      {state !== "hidden" && (
        <button
          type="button"
          data-testid="visibility-extend-7"
          onClick={extend}
          disabled={busy}
          className="inline-flex items-center gap-1 bg-white border border-current rounded-md px-2.5 py-1 font-bold hover:bg-opacity-80 disabled:opacity-50"
          title="Personel görünürlüğünü +7 gün uzat"
        >
          +7 gün uzat
        </button>
      )}
    </div>
  );
}


// =========================
// TagsRow — manual + rule-driven müvekkil chips (varlık/factoring/sigorta vs.)
// =========================
function TagsRow({ tags, autoTags, onChange, readOnly }) {
  const [input, setInput] = React.useState("");
  const autoSet = React.useMemo(
    () => new Set((autoTags || []).map((t) => (t || "").toLocaleLowerCase("tr"))),
    [autoTags]
  );
  const add = () => {
    const v = (input || "").trim();
    if (!v) return;
    const lower = v.toLocaleLowerCase("tr");
    if ((tags || []).some((t) => (t || "").toLocaleLowerCase("tr") === lower)) {
      setInput("");
      return;
    }
    onChange([...(tags || []), v]);
    setInput("");
  };
  const remove = (t) => {
    onChange((tags || []).filter((x) => x !== t));
  };
  if (!tags?.length && readOnly) return null;
  return (
    <div className="bg-sky-50 border border-sky-200 rounded-lg p-3" data-testid="prim-tags-row">
      <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700 mb-1.5 flex items-center gap-1.5">
        Etiketler
        {autoTags?.length > 0 && (
          <span className="text-[9px] font-semibold text-sky-500 normal-case">
            · {autoTags.length} otomatik kural eşleşmesi uygulandı
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 items-center">
        {(tags || []).length === 0 && (
          <span className="text-xs text-slate-400">Etiket yok</span>
        )}
        {(tags || []).map((t) => {
          const isAuto = autoSet.has((t || "").toLocaleLowerCase("tr"));
          return (
            <span
              key={t}
              data-testid={`prim-tag-chip-${t}`}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                isAuto
                  ? "bg-indigo-100 text-indigo-800 border-indigo-300"
                  : "bg-white text-slate-700 border-slate-300"
              }`}
              title={isAuto ? "Admin kuralı tarafından otomatik eklendi" : "Elle eklendi"}
            >
              {isAuto && <span className="text-[9px]">OTO</span>}
              # {t}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => remove(t)}
                  className="ml-0.5 text-slate-400 hover:text-rose-600"
                  title="Kaldır"
                  data-testid={`prim-tag-remove-${t}`}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        {!readOnly && (
          <div className="inline-flex items-center gap-1 ml-auto">
            <input
              data-testid="prim-tag-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="Yeni etiket"
              className="text-xs px-2 py-1 border border-slate-300 rounded-md"
            />
            <button
              type="button"
              onClick={add}
              data-testid="prim-tag-add-btn"
              className="text-xs font-semibold px-2 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded-md"
            >
              Ekle
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
